import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faWifi,
  faExclamationTriangle,
  faBatteryFull,
  faBatteryQuarter,
  faChevronDown,
  faChevronRight,
  faTag,
  faMapPin,
  faBuilding,
  faTimes,
  faMicrochip,
  faMap,
  faChartLine,
  faEnvelope,
} from '@fortawesome/free-solid-svg-icons'
import { DeviceInfo } from '../common/enums'
import { formatNumber } from '../common/utils'
import { DeviceStatus } from '../utils/deviceStatus'
import { api } from '../services'
import { DeviceLocationMap } from './DeviceLocationMap'
import DeviceConnectivitySection from './DeviceConnectivitySection'

const ALERT_HISTORY_LIMIT = 50

interface DeviceDetailPanelProps {
  device: DeviceInfo
  status: DeviceStatus
  lastReadingTimestamp?: number
  isLowBattery: boolean
  activeAlertCount: number
  onClear: () => void
}

interface AlertHistoryItem {
  id: number
  alertType: string
  message: string
  createdAt: string
}

interface SensorConfig {
  sensorName?: string
  sensorProductName?: string
  sensorModelName?: string
  sensorType?: string
  unitCategoryId?: number
  dateEffect?: string
}

const SENSOR_PORT_ORDER = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2', 'T0']

function formatTimeAgo(timestamp?: number | string): string {
  if (!timestamp) return 'never'
  const date =
    typeof timestamp === 'number' ? new Date(timestamp * 1000) : new Date(timestamp)
  if (isNaN(date.getTime())) return 'never'
  const diffMs = Date.now() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  if (diffMins < 1) return 'just now'
  if (diffMins < 60) return `${diffMins}m ago`
  const diffHours = Math.floor(diffMins / 60)
  if (diffHours < 24) return `${diffHours}h ago`
  return `${Math.floor(diffHours / 24)}d ago`
}

function formatTimestamp(timestamp: number | string | undefined): string {
  if (!timestamp) return ''
  const date =
    typeof timestamp === 'number' ? new Date(timestamp * 1000) : new Date(timestamp)
  if (isNaN(date.getTime())) return ''
  return date.toLocaleString()
}

function StatusCard({
  icon,
  iconBg,
  iconText,
  title,
  value,
  subtitle,
  onClick,
}: {
  icon: any
  iconBg: string
  iconText: string
  title: string
  value: string
  subtitle?: string
  onClick?: () => void
}) {
  const interactive = !!onClick
  const Element: any = interactive ? 'button' : 'div'
  return (
    <Element
      onClick={onClick}
      className={`bg-white rounded-lg shadow p-5 flex items-center gap-4 w-full text-left ${
        interactive ? 'hover:shadow-md hover:bg-gray-50 transition-all cursor-pointer' : ''
      }`}
    >
      <div className={`rounded-full p-3 ${iconBg} ${iconText}`}>
        <FontAwesomeIcon icon={icon} className="h-5 w-5" />
      </div>
      <div className="min-w-0">
        <p className="text-sm text-gray-500">{title}</p>
        <p className="text-2xl font-bold text-gray-900 truncate">{value}</p>
        {subtitle && <p className="text-xs text-gray-400 mt-0.5 truncate">{subtitle}</p>}
      </div>
    </Element>
  )
}

function IdentityRow({
  icon,
  label,
  value,
}: {
  icon: any
  label: string
  value: string | undefined | null
}) {
  if (value === undefined || value === null || value === '') return null
  return (
    <div className="flex items-start gap-3">
      <div className="rounded-full bg-gray-50 text-gray-500 p-2 flex-shrink-0">
        <FontAwesomeIcon icon={icon} className="h-3.5 w-3.5" />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-gray-500">{label}</p>
        <p className="text-sm font-medium text-gray-900 truncate" title={String(value)}>
          {value}
        </p>
      </div>
    </div>
  )
}

export default function DeviceDetailPanel({
  device,
  status,
  lastReadingTimestamp,
  isLowBattery,
  activeAlertCount,
  onClear,
}: DeviceDetailPanelProps) {
  const navigate = useNavigate()
  const [hardwareOpen, setHardwareOpen] = useState(true)
  const [batteryVoltage, setBatteryVoltage] = useState<number | null>(null)
  const [batteryLoading, setBatteryLoading] = useState(false)
  const [recentAlerts, setRecentAlerts] = useState<AlertHistoryItem[]>([])
  const [alertsLoading, setAlertsLoading] = useState(false)
  const [sensorConfigs, setSensorConfigs] = useState<Record<string, SensorConfig>>({})
  const [sensorConfigLoading, setSensorConfigLoading] = useState(false)
  const [deviceConfigDateEffect, setDeviceConfigDateEffect] = useState<string | null>(null)

  // Latest battery voltage. /battery-readings returns
  //   { data: { battery: { [unixTs]: voltage }, power: {...} } }
  // so we pick the entry with the highest timestamp. Fetch a wide window
  // (limit=1 with from=0) so devices that only recently came back online
  // still show their last known reading instead of N/A.
  useEffect(() => {
    let cancelled = false
    setBatteryLoading(true)
    setBatteryVoltage(null)
    const to = Math.floor(Date.now() / 1000)
    api
      .getLatestBatteryReading(device.sigfoxId, to)
      .then((result: any) => {
        if (cancelled) return
        const battery = result?.data?.battery
        if (!battery || typeof battery !== 'object') {
          setBatteryVoltage(null)
          return
        }
        const entries = Object.entries(battery) as Array<[string, number]>
        if (entries.length === 0) {
          setBatteryVoltage(null)
          return
        }
        entries.sort((a, b) => Number(b[0]) - Number(a[0]))
        const v = Number(entries[0][1])
        setBatteryVoltage(Number.isFinite(v) && v > 0 ? v : null)
      })
      .catch(() => {
        if (!cancelled) setBatteryVoltage(null)
      })
      .finally(() => {
        if (!cancelled) setBatteryLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [device.sigfoxId])

  // Sensor configuration (per-port) + most-recent device config timestamp.
  useEffect(() => {
    let cancelled = false
    setSensorConfigLoading(true)
    setSensorConfigs({})
    setDeviceConfigDateEffect(null)
    Promise.allSettled([
      api.getSensorConfig(device.sigfoxId),
      api.getDeviceConfig(device.sigfoxId, true),
    ])
      .then(([sensorRes, deviceRes]) => {
        if (cancelled) return
        if (sensorRes.status === 'fulfilled') {
          const data = (sensorRes.value as any)?.data
          if (data && typeof data === 'object') setSensorConfigs(data)
        }
        if (deviceRes.status === 'fulfilled') {
          const cfg = (deviceRes.value as any)?.config
          if (cfg?.dateEffect) setDeviceConfigDateEffect(cfg.dateEffect)
        }
      })
      .finally(() => {
        if (!cancelled) setSensorConfigLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [device.sigfoxId])

  // Recent alert history scoped to this device.
  useEffect(() => {
    let cancelled = false
    setAlertsLoading(true)
    setRecentAlerts([])
    api
      .getAlertHistory({ deviceId: device.deviceId, limit: ALERT_HISTORY_LIMIT })
      .then((result: any) => {
        if (cancelled) return
        const items = Array.isArray(result?.data) ? result.data : []
        setRecentAlerts(items)
      })
      .catch(() => {
        if (!cancelled) setRecentAlerts([])
      })
      .finally(() => {
        if (!cancelled) setAlertsLoading(false)
      })
  }, [device.deviceId])

  const statusLabel = useMemo(() => {
    if (status === 'online') return 'Online'
    if (status === 'offline') return 'Offline'
    return 'Pending'
  }, [status])

  const statusColors = useMemo(() => {
    if (status === 'online') return { iconBg: 'bg-green-50', iconText: 'text-green-600' }
    if (status === 'offline') return { iconBg: 'bg-red-50', iconText: 'text-red-600' }
    return { iconBg: 'bg-gray-100', iconText: 'text-gray-500' }
  }, [status])

  const batteryColors = isLowBattery
    ? { iconBg: 'bg-amber-50', iconText: 'text-amber-600' }
    : { iconBg: 'bg-green-50', iconText: 'text-green-600' }

  // Latest sensor-config DATE_EFFECT across all ports — surrogate for
  // "last sensor reconfiguration". Pair with the device-config DATE_EFFECT
  // (board-level firmware/site changes) to give admins a real last-touched.
  const latestSensorConfigDate = useMemo(() => {
    let latest: number | null = null
    for (const cfg of Object.values(sensorConfigs)) {
      if (!cfg?.dateEffect) continue
      const t = new Date(cfg.dateEffect).getTime()
      if (!isNaN(t) && (latest === null || t > latest)) latest = t
    }
    return latest
  }, [sensorConfigs])

  const populatedPorts = useMemo(() => {
    return SENSOR_PORT_ORDER.filter((port) => {
      const cfg = sensorConfigs[port]
      return cfg && cfg.sensorProductName && cfg.sensorProductName.toLowerCase() !== 'none'
    })
  }, [sensorConfigs])

  const hasLocation = device.coordinateNSValue !== 0 && device.coordinateEWValue !== 0

  return (
    <div className="space-y-4" data-testid="device-detail-panel">
      {/* Context chip + CTA bar — admin can dismiss the selection or jump to
          another page that already accepts ?sigfoxId=. */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="text-xs uppercase tracking-wide text-gray-400">Selected device</span>
          <button
            onClick={onClear}
            className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-sm font-medium hover:bg-blue-100 transition-colors"
            aria-label="Clear selected device"
          >
            <span>{device.deviceName || device.sigfoxId}</span>
            <FontAwesomeIcon icon={faTimes} className="h-3 w-3" />
          </button>
          {isLowBattery && (
            <span className="inline-flex items-center gap-1 bg-amber-50 text-amber-700 px-2 py-1 rounded-full text-xs font-medium">
              <FontAwesomeIcon icon={faBatteryQuarter} className="h-3 w-3" />
              Low battery
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate(`/map?sigfoxId=${device.sigfoxId}`)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
          >
            <FontAwesomeIcon icon={faMap} className="h-3.5 w-3.5 text-gray-500" />
            View on Map
          </button>
          <button
            onClick={() => navigate(`/charts?sigfoxId=${device.sigfoxId}`)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
          >
            <FontAwesomeIcon icon={faChartLine} className="h-3.5 w-3.5 text-gray-500" />
            Open Charts
          </button>
          <button
            onClick={() => navigate(`/messages?sigfoxId=${device.sigfoxId}`)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
          >
            <FontAwesomeIcon icon={faEnvelope} className="h-3.5 w-3.5 text-gray-500" />
            Messages
          </button>
        </div>
      </div>

      {/* Status row — three cards mirroring StatCard */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatusCard
          icon={faWifi}
          {...statusColors}
          title="Status"
          value={statusLabel}
          subtitle={
            lastReadingTimestamp
              ? `Last reading ${formatTimeAgo(lastReadingTimestamp)}`
              : 'No readings yet'
          }
        />
        <StatusCard
          icon={faExclamationTriangle}
          iconBg={activeAlertCount > 0 ? 'bg-amber-50' : 'bg-gray-100'}
          iconText={activeAlertCount > 0 ? 'text-amber-600' : 'text-gray-500'}
          title="Active Alerts"
          value={String(activeAlertCount)}
        />
        <StatusCard
          icon={isLowBattery ? faBatteryQuarter : faBatteryFull}
          {...batteryColors}
          title="Battery"
          value={
            batteryLoading
              ? '…'
              : batteryVoltage !== null
                ? `${formatNumber(batteryVoltage, 2)} V`
                : 'N/A'
          }
          subtitle={isLowBattery ? 'Below threshold' : 'View battery history'}
          onClick={() => navigate(`/charts?sigfoxId=${device.sigfoxId}&battery=1`)}
        />
      </div>

      {/* Identity and sensor configuration sit side by side on wide screens —
          neither is tall enough to earn a full row of its own. The grid's
          default `stretch` is left in place so both cards end on the same
          line; the shorter one carries the slack as whitespace, which reads
          better than two cards ending at different heights.
          Below lg they stack, which is what the outer space-y-4 already did. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* Identity card */}
      <div className="bg-white rounded-lg shadow p-5">
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <IdentityRow icon={faBuilding} label="Account" value={device.accountName} />
          <IdentityRow
            icon={faMapPin}
            label="Site"
            value={
              device.siteName && device.siteCode
                ? `${device.siteName} · ${device.siteCode}`
                : device.siteName || (device.siteCode ? String(device.siteCode) : null)
            }
          />
          <IdentityRow icon={faTag} label="Sigfox ID" value={device.sigfoxId} />
          <IdentityRow icon={faTag} label="Device Name" value={device.deviceName} />
          <IdentityRow
            icon={faMicrochip}
            label="Mode (DEV_APP)"
            value={
              device.deviceApp !== undefined && device.deviceApp !== null
                ? String(device.deviceApp)
                : null
            }
          />
          <IdentityRow
            icon={faTag}
            label="Last configured"
            value={
              deviceConfigDateEffect ? formatTimestamp(deviceConfigDateEffect) : null
            }
          />
        </div>

        {/* Hardware details — collapsed by default */}
        <button
          onClick={() => setHardwareOpen((v) => !v)}
          className="mt-4 flex items-center gap-2 text-xs font-medium text-gray-500 uppercase tracking-wide hover:text-gray-700"
          aria-expanded={hardwareOpen}
        >
          <FontAwesomeIcon
            icon={hardwareOpen ? faChevronDown : faChevronRight}
            className="text-gray-400 h-3 w-3"
          />
          Hardware details
        </button>
        {hardwareOpen && (
          <div className="mt-3 grid grid-cols-1 xl:grid-cols-2 gap-4">
            <IdentityRow
              icon={faTag}
              label="Serial"
              value={device.serialNumber ? String(device.serialNumber) : null}
            />
            <IdentityRow
              icon={faTag}
              label="Hardware version"
              value={device.hardwareNumber ? String(device.hardwareNumber) : null}
            />
            <IdentityRow
              icon={faTag}
              label="Firmware"
              value={device.firmwareVersion ? String(device.firmwareVersion) : null}
            />
            <IdentityRow
              icon={faTag}
              label="Date code"
              value={device.dateCode}
            />
          </div>
        )}
      </div>

      {/* Sensor configuration — one row per populated port. Empty bays are
          skipped. The latest sensor-cfg dateEffect surfaces when the most
          recent sensor change happened. */}
      <div className="bg-white rounded-lg shadow p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-gray-700 flex items-center gap-2">
            <FontAwesomeIcon icon={faMicrochip} className="text-gray-500" />
            Sensor Configuration
          </h3>
          {latestSensorConfigDate && (
            <span className="text-xs text-gray-400">
              Last updated {formatTimeAgo(latestSensorConfigDate / 1000)}
            </span>
          )}
        </div>
        {sensorConfigLoading ? (
          <p className="text-sm text-gray-400">Loading...</p>
        ) : populatedPorts.length === 0 ? (
          <p className="text-sm text-gray-400">No sensors configured</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {populatedPorts.map((port) => {
              const cfg = sensorConfigs[port]
              return (
                <li key={port} className="py-2 flex items-center gap-3">
                  <span className="inline-flex items-center justify-center bg-gray-100 text-gray-700 text-xs font-bold rounded w-9 h-7 flex-shrink-0">
                    {port}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {cfg.sensorProductName || 'Unknown'}
                    </p>
                    {cfg.sensorModelName && (
                      <p className="text-xs text-gray-500 truncate">{cfg.sensorModelName}</p>
                    )}
                  </div>
                  {cfg.dateEffect && (
                    <span className="text-xs text-gray-400 flex-shrink-0">
                      {formatTimeAgo(new Date(cfg.dateEffect).getTime() / 1000)}
                    </span>
                  )}
                </li>
              )
            })}
          </ul>
        )}
      </div>
      </div>

      {/* Signal for everyone, missed messages for admins, under one range. */}
      <DeviceConnectivitySection sigfoxId={device.sigfoxId} deviceId={device.deviceId} />

      {/* Location and alert history share a row: the map reads fine at half
          width, and alert history is usually one line. When there is no map,
          alert history takes the full row alone. The alert list scrolls at a
          height that keeps its card about level with the 240px map. Below lg
          they stack. */}
      <div className={`grid grid-cols-1 gap-4 ${hasLocation ? 'lg:grid-cols-2' : ''}`}>
        {/* Device location — small embedded map. Skipped when coordinates are
            missing or zero (which is the common "unset" sentinel here). */}
        {hasLocation && (
          <div className="bg-white rounded-lg shadow p-5">
            <h3 className="text-lg font-semibold text-gray-700 flex items-center gap-2 mb-3">
              <FontAwesomeIcon icon={faMap} className="text-gray-500" />
              Location
            </h3>
            <div className="rounded-md overflow-hidden border border-gray-200">
              <DeviceLocationMap
                lat={device.coordinateNSValue}
                lng={device.coordinateEWValue}
                title={device.deviceName || device.sigfoxId}
              />
            </div>
            <p className="text-xs text-gray-400 mt-2">
              {device.coordinateNSValue.toFixed(5)}, {device.coordinateEWValue.toFixed(5)}
            </p>
          </div>
        )}

        {/* Alert history — scoped to this device only.
            Distinct from the account-wide "Recent Alerts" card on the Dashboard
            above; this is the full history filtered to the selected device. */}
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-lg font-semibold mb-3 text-gray-700 flex items-center gap-2">
            <FontAwesomeIcon icon={faExclamationTriangle} className="text-amber-500" />
            Alert History
          </h3>
          {alertsLoading ? (
            <p className="text-sm text-gray-400">Loading...</p>
          ) : recentAlerts.length === 0 ? (
            <p className="text-sm text-gray-400">No alert history for this device</p>
          ) : (
            <ul className="divide-y divide-gray-100 max-h-64 overflow-y-auto">
              {recentAlerts.map((alert) => (
                <li key={alert.id} className="py-3 flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">
                      {alert.message}
                    </p>
                  </div>
                  <div className="ml-4 flex-shrink-0 text-right">
                    <span className="inline-block px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-700">
                      {alert.alertType}
                    </span>
                    <p className="text-xs text-gray-400 mt-1">
                      {formatTimestamp(alert.createdAt)}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

    </div>
  )
}
